/**
 * Correlation-id propagation (TI-316 [P1-S4]).
 *
 * Adds `X-Request-Id` (a fresh UUID per call) and, when a session is active,
 * `X-Session-Id` to outbound calls to the video-streaming-server (VSS) so a
 * single request can be traced end-to-end across FE → VSS → UM → bot-backend
 * logs.
 *
 * The Java meeting controller / server is the authority for `sessionId`; this
 * client only reads it (from the join URL or, when embedded, from the parent
 * via postMessage) and forwards it. Missing/malformed values never crash the
 * room — we warn and continue.
 *
 * Additive + safe: caller-supplied headers always win; the returned promise is
 * a thin pass-through to `fetch`.
 */

export const REQUEST_ID_HEADER = "X-Request-Id";
export const SESSION_ID_HEADER = "X-Session-Id";

/**
 * Module-level session id. Set once the room/session is known so call sites that
 * don't thread `sessionId` through still emit `X-Session-Id`. A per-call
 * `options.sessionId` overrides it.
 */
let activeSessionId = null;

export function setActiveSessionId(sessionId) {
  const trimmed = typeof sessionId === "string" ? sessionId.trim() : sessionId;
  activeSessionId = trimmed ? String(trimmed) : null;
}

export function getActiveSessionId() {
  return activeSessionId;
}

/** Fresh UUID per request; degrades gracefully where crypto.randomUUID is absent. */
export function generateRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Non-cryptographic, trace-only fallback.
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Case-insensitive header presence check across plain-object / Headers / array shapes. */
function hasHeader(headers, name) {
  if (!headers) return false;
  const target = name.toLowerCase();
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    return headers.has(name);
  }
  if (Array.isArray(headers)) {
    return headers.some(([k]) => String(k).toLowerCase() === target);
  }
  return Object.keys(headers).some((k) => k.toLowerCase() === target);
}

/**
 * Returns correlation headers as a plain object, merged on top of `base`
 * (without clobbering anything the caller already set). Use this for call sites
 * that build their own headers object (e.g. `authJsonHeaders`).
 *
 * @param {Record<string,string>} [base] existing headers
 * @param {string|null} [sessionIdOverride] per-call session id (else module-level)
 */
export function correlationHeaders(base = {}, sessionIdOverride) {
  const out = { ...base };

  if (!hasHeader(base, REQUEST_ID_HEADER)) {
    out[REQUEST_ID_HEADER] = generateRequestId();
  }

  const sessionId =
    sessionIdOverride !== undefined ? sessionIdOverride : activeSessionId;
  if (sessionId && !hasHeader(base, SESSION_ID_HEADER)) {
    out[SESSION_ID_HEADER] = String(sessionId);
  }

  return out;
}

/**
 * Drop-in `fetch` replacement that injects correlation headers without clobbering
 * any header the caller already set. `options.sessionId` overrides the
 * module-level active session id for this call.
 */
export function apiFetch(url, options = {}) {
  const { sessionId, headers: callerHeaders, ...rest } = options;

  const headers = new Headers(callerHeaders || undefined);

  if (!hasHeader(callerHeaders, REQUEST_ID_HEADER)) {
    headers.set(REQUEST_ID_HEADER, generateRequestId());
  }

  const resolvedSessionId = sessionId !== undefined ? sessionId : activeSessionId;
  if (resolvedSessionId && !hasHeader(callerHeaders, SESSION_ID_HEADER)) {
    headers.set(SESSION_ID_HEADER, String(resolvedSessionId));
  }

  return fetch(url, { ...rest, headers });
}

/** Reads the echoed `X-Request-Id` back off a Response (for logging/correlation). */
export function getResponseRequestId(response) {
  return response && response.headers ? response.headers.get(REQUEST_ID_HEADER) : null;
}

/**
 * Reads `sessionId` from a query string (defaults to the live `window.location`).
 * Accepts the canonical `sessionId` plus a couple of casings already used by the
 * join pages for other params. Returns `null` when absent/blank.
 *
 * @param {string} [search] e.g. `?sessionId=abc`
 */
export function readSessionIdFromQuery(search) {
  try {
    const qs =
      search !== undefined
        ? search
        : typeof window !== "undefined"
        ? window.location.search
        : "";
    const params = new URLSearchParams(qs);
    const raw =
      params.get("sessionId") || params.get("sessionid") || params.get("session_id");
    const trimmed = raw ? raw.trim() : "";
    return trimmed || null;
  } catch (err) {
    console.warn("[correlation] could not parse sessionId from query", err);
    return null;
  }
}

/**
 * Allowed parent origins for the iframe `postMessage` contract. The session room
 * is embedded by the main app (interviews-ui); only these origins may set the
 * session id. Extend via `REACT_APP_PARENT_ORIGINS` (comma-separated).
 */
function allowedParentOrigins() {
  const fromEnv = (process.env.REACT_APP_PARENT_ORIGINS || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const defaults = [
    "https://www.theinterviews.ai",
    "https://theinterviews.ai",
    "https://develop.theinterviews.ai",
  ];
  return new Set([...defaults, ...fromEnv]);
}

/**
 * True when `origin` is an allowed embedding-parent origin. Single source for
 * every parent→room postMessage surface: the `ti:session` listener here and
 * the `ti:auth` listener in `mintAuth.js` (TI-974) validate against the same
 * allow-list.
 */
export function isAllowedParentOrigin(origin) {
  return allowedParentOrigins().has(origin);
}

/**
 * Message contract (parent → embedded room):
 *   { type: "ti:session", sessionId: "<id>" }
 *
 * The handler validates `event.origin` against the allow-list and the message
 * shape before calling `setActiveSessionId`. Arbitrary-origin or malformed
 * messages are ignored (warned in dev). Returns a teardown function.
 */
export function installParentSessionListener() {
  if (typeof window === "undefined") return () => {};

  const onMessage = (event) => {
    if (!isAllowedParentOrigin(event.origin)) {
      // Silently ignore unknown origins — postMessage is a public surface.
      return;
    }
    const data = event.data;
    if (!data || typeof data !== "object" || data.type !== "ti:session") {
      return;
    }
    const sessionId = typeof data.sessionId === "string" ? data.sessionId.trim() : "";
    if (!sessionId) {
      console.warn("[correlation] ti:session message had no sessionId");
      return;
    }
    setActiveSessionId(sessionId);
  };

  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}

/**
 * Convenience bootstrap for app entry: read sessionId from the URL (if present)
 * and install the postMessage listener for the embedded case. Idempotent-ish —
 * a later postMessage overrides the URL value (last-writer-wins), which is the
 * intended precedence for embeds.
 */
export function installCorrelationBootstrap() {
  const fromUrl = readSessionIdFromQuery();
  if (fromUrl) {
    setActiveSessionId(fromUrl);
  }
  return installParentSessionListener();
}
